import React, { useEffect, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { 
  Plus, 
  FileText, 
  Edit2, 
  Clock, 
  Calendar,
  Trash2,
  Search,
  Filter,
  Tag,
  MoreVertical,
  X
} from "lucide-react";
import { format } from "date-fns";
import { motion, AnimatePresence } from "framer-motion";

type SettingsTab = "published" | "pending_review" | "draft" | "rejected";
type SortOption = "newest" | "oldest" | "title_az" | "title_za";

import { useBlogPosts } from "../hooks/useBlogPosts";
import { useAuthStore } from "../store/authStore";
import { DashboardLayout } from "../layouts/DashboardLayout";
import Preloader from "../components/ui/Preloader";

export const BlogDashboardPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<SettingsTab | "all">("all");
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState<SortOption>("newest");
  const [showFilters, setShowFilters] = useState(false);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const userInfo = localStorage.getItem('user');
  const userInfoParsed = userInfo ? JSON.parse(userInfo) : null;
  const id_practice = userInfoParsed?.['custom:id_practice'];
  const navigate = useNavigate();
  const { posts: rawPosts, loading, error } = useBlogPosts( { query: id_practice ? `id_practice=${id_practice}` : undefined } );

  // This renders before the refetch resolves, so it can see whatever the store
  // already holds — including entries a create response left behind. Normalise
  // once here so nothing downstream has to defend itself.
  const posts = Array.isArray(rawPosts) ? rawPosts.filter(Boolean) : [];


  const isAdmin = useAuthStore(
    (state) => state.user?.email === "info@therapistdirect.org"
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  const handleCreatePost = () => {
    navigate('/practice/blog/create');
  };

  const handleEditPost = (postId: string) => {
    navigate(`/practice/blog/edit/${postId}`);
  };

  const handleViewPost = (postId: string) => {
    navigate(`/blog/${postId}`);
  };

  const handleDeletePost = (postId: string) => {
    if (window.confirm("Are you sure you want to delete this post?")) {
    }
  };

  // Extract all unique tags from posts
  const allTags = Array.from(
    new Set(
      posts
        .flatMap((post: any) => post.tags || [])
        .filter(Boolean)
    )
  );

  // Get statistics
  const getStats = () => {
    const published = posts.filter((post: any) => post.status === "published").length;
    const drafts = posts.filter((post: any) => post.status === "draft").length;
    const pending = posts.filter((post: any) => post.status === "pending_review").length;
    const rejected = posts.filter((post: any) => post.status === "rejected").length;
    const total = posts.length;

    return { published, drafts, pending, rejected, total };
  };

  const stats = getStats();

  // Filter and sort posts based on current filters
  const getFilteredPosts = () => {
    return posts
      .filter((post: any) => {
        const statusMatch = activeTab === "all" || post.status === activeTab;
        const searchMatch = searchQuery
          ? (post.title ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
            (post.excerpt && post.excerpt.toLowerCase().includes(searchQuery.toLowerCase()))
          : true;
        const tagMatch = selectedTags.length > 0 
          ? selectedTags.every(tag => post.tags && post.tags.includes(tag))
          : true;
        
        return statusMatch && searchMatch && tagMatch;
      })
      .sort((a: any, b: any) => {
        switch (sortBy) {
          case "newest":
            return (b.created_at || 0) - (a.created_at || 0);
          case "oldest":
            return (a.created_at || 0) - (b.created_at || 0);
          case "title_az":
            return (a.title ?? '').localeCompare(b.title ?? '');
          case "title_za":
            return (b.title ?? '').localeCompare(a.title ?? '');
          default:
            return 0;
        }
      });
  };

  if (isLoading) {
    return <Preloader />;
  }

  if (loading) {
    return (
      <DashboardLayout>
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="flex justify-center items-center h-full"
        >
          <motion.div 
            animate={{ 
              scale: [1, 1.1, 1],
              transition: { repeat: Infinity, duration: 1 }
            }}
            className="animate-pulse text-purple-600"
          >
            Loading...
          </motion.div>
        </motion.div>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-red-600 p-6 bg-red-50 rounded-lg"
        >
          Error: {error}
        </motion.div>
      </DashboardLayout>
    );
  }

  if (isAdmin) {
    return <Navigate to="/admin/blog" />;
  }

  const filteredPosts = getFilteredPosts();

  return (
    <DashboardLayout>
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="flex-1 overflow-auto"
      >
        <div className="p-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Blog Management</h1>
              <p className="text-gray-600">Create, manage and publish blog articles</p>
            </div>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleCreatePost}
              className="flex items-center gap-2 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700"
            >
              <Plus className="w-4 h-4" />
              Create New Post
            </motion.button>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="rounded-xl p-6 shadow-sm border bg-white border-gray-200"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-purple-100">
                  <FileText className="w-5 h-5 text-purple-600" />
                </div>
                <div className="px-2 py-1 rounded text-xs font-medium bg-purple-100 text-purple-700">
                  {stats.drafts}
                </div>
              </div>
              <div className="text-lg font-semibold mb-1 text-gray-900">Draft</div>
              <div className="text-sm text-gray-600">Unpublished articles</div>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="rounded-xl p-6 shadow-sm border bg-white border-gray-200"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-green-100">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5 text-green-600">
                    <path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0"></path>
                    <circle cx="12" cy="12" r="3"></circle>
                  </svg>
                </div>
                <div className="px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-700">
                  {stats.published}
                </div>
              </div>
              <div className="text-lg font-semibold mb-1 text-gray-900">Published</div>
              <div className="text-sm text-gray-600">Live articles</div>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="rounded-xl p-6 shadow-sm border bg-white border-gray-200"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-orange-100">
                  <Clock className="w-5 h-5 text-orange-600" />
                </div>
                <div className="px-2 py-1 rounded text-xs font-medium bg-orange-100 text-orange-700">
                  {stats.pending}
                </div>
              </div>
              <div className="text-lg font-semibold mb-1 text-gray-900">Pending Review</div>
              <div className="text-sm text-gray-600">Awaiting approval</div>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="rounded-xl p-6 shadow-sm border bg-white border-gray-200"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-blue-100">
                  <Calendar className="w-5 h-5 text-blue-600" />
                </div>
                <div className="px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-700">
                  {stats.rejected}
                </div>
              </div>
              <div className="text-lg font-semibold mb-1 text-gray-900">Rejected</div>
              <div className="text-sm text-gray-600">Rejected articles</div>
            </motion.div>
          </div>

          {/* Filter Buttons */}
          <div className="mb-6">
            <div className="flex flex-wrap gap-2">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setActiveTab("all")}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeTab === "all"
                    ? "bg-purple-600 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                All Posts ({stats.total})
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setActiveTab("draft")}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeTab === "draft"
                    ? "bg-purple-600 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                Draft ({stats.drafts})
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setActiveTab("published")}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeTab === "published"
                    ? "bg-purple-600 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                Published ({stats.published})
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setActiveTab("pending_review")}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeTab === "pending_review"
                    ? "bg-purple-600 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                Pending Review ({stats.pending})
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setActiveTab("rejected")}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeTab === "rejected"
                    ? "bg-purple-600 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                Rejected ({stats.rejected})
              </motion.button>
            </div>
          </div>

          {/* Main Content */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search posts by title or content..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery("")}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
                
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as SortOption)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="newest">Newest First</option>
                  <option value="oldest">Oldest First</option>
                  <option value="title_az">Title A-Z</option>
                  <option value="title_za">Title Z-A</option>
                </select>
                
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setShowFilters(!showFilters)}
                  className={`flex items-center gap-2 px-4 py-2 text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 ${
                    showFilters || selectedTags.length > 0
                      ? "border-purple-500 bg-purple-50 text-purple-700"
                      : ""
                  }`}
                >
                  <Filter className="w-4 h-4" />
                  Filter
                  {selectedTags.length > 0 && (
                    <span className="bg-purple-200 text-purple-800 text-xs rounded-full px-2 py-1">
                      {selectedTags.length}
                    </span>
                  )}
                </motion.button>
              </div>
              
              <AnimatePresence>
                {showFilters && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    className="mt-4 pt-4 border-t border-gray-200"
                  >
                    <div className="flex items-center justify-between mb-4">
                      <div className="font-medium flex items-center">
                        <Tag className="h-5 w-5 mr-2 text-purple-600" />
                        Filter by Tags
                      </div>
                      {selectedTags.length > 0 && (
                        <button
                          onClick={() => setSelectedTags([])}
                          className="text-sm text-purple-600 hover:text-purple-800 font-medium"
                        >
                          Clear all
                        </button>
                      )}
                    </div>
                    
                    <div className="flex flex-wrap gap-2">
                      {allTags.map((tag: string) => (
                        <motion.button
                          key={tag}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => {
                            setSelectedTags(prev => 
                              prev.includes(tag)
                                ? prev.filter(t => t !== tag)
                                : [...prev, tag]
                            );
                          }}
                          className={`px-3 py-2 text-sm rounded-lg transition-all font-medium ${
                            selectedTags.includes(tag)
                              ? "bg-purple-600 text-white shadow-md"
                              : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                          }`}
                        >
                          {tag}
                        </motion.button>
                      ))}
                      {allTags.length === 0 && (
                        <span className="text-sm text-gray-500 py-2">No tags available</span>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <div className="p-6">
              {filteredPosts.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <FileText className="w-8 h-8 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No posts found</h3>
                  <p className="text-gray-600 mb-6">
                    {searchQuery 
                      ? "Try adjusting your search terms" 
                      : activeTab === "all" 
                        ? "No posts found" 
                        : `No ${activeTab.replace('_', ' ')} posts found`
                    }
                  </p>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleCreatePost}
                    className="bg-purple-600 hover:bg-purple-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
                  >
                    Add your first article
                  </motion.button>
                </div>
              ) : (
                <motion.div 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ staggerChildren: 0.1 }}
                  className="grid gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-3"
                >
                  {filteredPosts.map((post: any, index: number) => (
                    <motion.div 
                      key={post.id} 
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="group bg-white rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 border border-gray-100 overflow-hidden"
                    >
                      {post.coverImage && (
                        <div className="overflow-hidden aspect-video w-full">
                          <img 
                            src={post.coverImage} 
                            alt={post.title} 
                            className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-300"
                            onError={(e) => {
                              (e.target as HTMLImageElement).src = "/api/placeholder/400/225";
                            }}
                          />
                        </div>
                      )}
                      
                      <div className="p-6 flex-grow">
                        <div className="flex items-center justify-between mb-3">
                          <span className={`text-xs px-3 py-1 rounded-full font-medium ${
                            post.status === 'published' ? 'bg-green-100 text-green-700' : 
                            post.status === 'draft' ? 'bg-blue-100 text-blue-700' : 
                            post.status === 'rejected' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'
                          }`}>
                            {post.status === 'published' ? 'Published' : 
                            post.status === 'draft' ? 'Draft' : 
                            post.status === 'rejected' ? 'Rejected' : 'Pending Review'}
                          </span>
                          
                          <div className="flex items-center space-x-2 text-xs text-gray-500">
                            {post.readTime && (
                              <span className="flex items-center">
                                <Clock className="w-3 h-3 mr-1" />
                                {post.readTime} min
                              </span>
                            )}
                            <span className="flex items-center">
                              <Calendar className="w-3 h-3 mr-1" />
                              {post.created_at ? format(new Date(post.created_at * 1000), "MMM d") : "No date"}
                            </span>
                          </div>
                        </div>
                        
                        <h3 className="text-lg font-bold text-gray-900 mb-2 line-clamp-2 group-hover:text-purple-600 transition-colors">
                          {post.title}
                        </h3>
                        
                        <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                          {post.excerpt || post.description || "No description available"}
                        </p>

                        {post.status === 'rejected' && (post.rejection_reason || post.review_results) && (
                          <div className="mb-4 rounded-lg border border-red-100 bg-red-50 p-4">
                            <p className="text-sm font-medium text-red-700">Rejection reason</p>
                            <p className="mt-1 text-sm text-red-600 whitespace-pre-line">
                              {post.rejection_reason || post.review_results}
                            </p>
                          </div>
                        )}
 
                        {post.tags && post.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1 mb-4">
                            {post.tags.slice(0, 3).map((tag: string) => (
                              <span 
                                key={tag} 
                                className="px-2 py-1 text-xs bg-purple-100 text-purple-700 rounded-full hover:bg-purple-200 transition-colors cursor-pointer"
                              >
                                {tag}
                              </span>
                            ))}
                            {post.tags.length > 3 && (
                              <span className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded-full">
                                +{post.tags.length - 3} more
                              </span>
                            )}
                          </div>
                        )}
                        
                        <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                          <div className="flex space-x-2">
                            <motion.button
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                              onClick={() => handleViewPost(post.slug)}
                              className="p-2 rounded-full bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors"
                              title="View Post"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4">
                                <path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0"></path>
                                <circle cx="12" cy="12" r="3"></circle>
                              </svg>
                            </motion.button>
                            
                            <motion.button
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                              onClick={() => handleEditPost(post.id)}
                              className="p-2 rounded-full bg-green-50 text-green-600 hover:bg-green-100 transition-colors"
                              title="Edit Post"
                            >
                              <Edit2 className="h-4 w-4" />
                            </motion.button>
                            
                            <motion.button
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                              onClick={() => handleDeletePost(post.id)}
                              className="p-2 rounded-full bg-red-50 text-red-600 hover:bg-red-100 transition-colors"
                              title="Delete Post"
                            >
                              <Trash2 className="h-4 w-4" />
                            </motion.button>
                          </div>
                          
                          <button className="p-2 rounded-full hover:bg-gray-100 transition-colors">
                            <MoreVertical className="h-4 w-4 text-gray-400" />
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </motion.div>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    </DashboardLayout>
  );
};
